import {
  FloatingArrow,
  FloatingPortal,
  Middleware,
  OpenChangeReason,
  Placement,
  arrow,
  autoPlacement,
  autoUpdate,
  offset as offsetMiddleware,
  useFloating,
} from "@floating-ui/react";
import React, {
  FC,
  HTMLAttributes,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useRef,
  useState,
} from "react";
import { useTombac } from "../../shared";
import { useOutsideClick } from "../../shared/useOutsideClick";
import { Text } from "../Typography";
import { StyledTooltip } from "./TooltipStyles";

export interface TooltipControlRenderProps {
  /** Open the tooltip. */
  open: () => void;
  /** Close the tooltip. */
  close: () => void;
  /** Toggle the tooltip. */
  toggle: () => void;
}

export interface TooltipChildrenRenderProps extends TooltipControlRenderProps {
  ref: (element: HTMLElement | null) => void;
}

export interface TooltipCommonProps
  extends Omit<HTMLAttributes<HTMLElement>, "children" | "content"> {
  /** Should return reference element of the tooltip. */
  children: (
    props: TooltipChildrenRenderProps & TooltipControlRenderProps
  ) => ReactNode;
  /** Set the offset of the tooltip (default = [0, 12]). */
  offset?: [number, number];
  /** Callback function called when the tooltip opens or closes. */
  onOpenChange?: (
    nextOpen: boolean,
    event?: Event,
    reason?: OpenChangeReason
  ) => void;
  /** Placement for the tooltip. Use "auto" to let the component decide (default = "auto"). */
  placement?: Placement | "auto";
  /** Determines the size of the padding (default = 'm'). */
  size?: "s" | "m";
  /** Whether to portal the tooltip to the end of the document body (default = false). */
  usePortal?: boolean;
  /** Color variant. 'default' is white, 'inverted' black and 'danger' red (default = 'default'). */
  variant?: "default" | "inverted" | "danger";
}

type TooltipUncontrolledProps = {
  /** Whether to close the tooltip when clicked outside (default = true). */
  closeOnOutsideClick?: boolean;
  /** The content of the tooltip. */
  content:
    | string
    | ReactElement
    | ((props: TooltipControlRenderProps) => ReactNode);
  isOpen?: never;
  /**
   * A callback function called when the tooltip closes.
   * @deprecated Use `onOpenChange` instead.
   */
  onClose?: () => void;
  /**
   * A callback function called when the tooltip opens.
   * @deprecated Use `onOpenChange` instead.
   */
  onOpen?: () => void;
};

type TooltipControlledProps = {
  closeOnOutsideClick?: never;
  /** The content of the tooltip. */
  content: ReactNode;
  /** Whether the tooltip is open or not. If provided, the tooltip will be controlled. */
  isOpen: boolean;
  onClose?: never;
  onOpen?: never;
};

export type TooltipProps = TooltipCommonProps &
  (TooltipUncontrolledProps | TooltipControlledProps);

export const Tooltip: FC<TooltipProps> = ({
  children,
  closeOnOutsideClick = true,
  content,
  isOpen: isOpenProp,
  offset: offsetProp = [0, 12],
  onClose,
  onOpen,
  onOpenChange,
  placement = "auto",
  size = "m",
  usePortal = false,
  variant = "default",
  ...rest
}) => {
  const tombac = useTombac();

  const isControlled = typeof isOpenProp === "boolean";

  const [isOpenState, setIsOpen] = useState(false);
  const isOpen = isControlled ? isOpenProp : isOpenState;

  const arrowRef = useRef<SVGSVGElement>(null);
  const arrowHeight = 5;
  const arrowWidth = arrowHeight * 2;
  const arrowFill =
    variant === "danger"
      ? tombac.color("danger", 400)
      : variant === "inverted"
      ? tombac.color("neutral", 900)
      : tombac.color("neutral", 100);

  const middleware: Middleware[] = [
    offsetMiddleware({
      crossAxis: offsetProp[0],
      mainAxis: offsetProp[1],
    }),
  ];

  if (placement === "auto") {
    middleware.push(autoPlacement());
  }

  middleware.push(arrow({ element: arrowRef }));

  const handleOpenChange = (
    nextOpen: boolean,
    event?: Event,
    reason?: OpenChangeReason
  ) => {
    setIsOpen(nextOpen);
    onOpenChange?.(nextOpen, event, reason);
    if (!isControlled) {
      if (nextOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    }
  };

  const { context, floatingStyles, refs } = useFloating({
    middleware,
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement: placement !== "auto" ? placement : undefined,
    whileElementsMounted: autoUpdate,
  });

  useOutsideClick(refs.floating.current, () => handleOpenChange(false), {
    enabled: closeOnOutsideClick,
    excluded: [refs.domReference.current],
  });

  const controlFns = {
    open: () => handleOpenChange(true),
    close: () => handleOpenChange(false),
    toggle: () => handleOpenChange(!isOpen),
  };

  return (
    <>
      {children({
        ...controlFns,
        ref: refs.setReference,
      })}
      {isOpen && (
        <ConditionalPortal usePortal={usePortal}>
          <StyledTooltip
            ref={refs.setFloating}
            style={floatingStyles}
            {...{ variant, size }}
            {...rest}
          >
            {typeof content === "string" ? (
              // Padding top applied to compensate Noway font offset.
              <Text $color="inherit" $pt="0.2em">
                {content}
              </Text>
            ) : typeof content === "function" ? (
              content(controlFns)
            ) : (
              content
            )}
            <FloatingArrow
              context={context}
              fill={arrowFill}
              height={arrowHeight}
              ref={arrowRef}
              width={arrowWidth}
            />
          </StyledTooltip>
        </ConditionalPortal>
      )}
    </>
  );
};

const ConditionalPortal: FC<PropsWithChildren<{ usePortal: boolean }>> = ({
  usePortal,
  children,
}) =>
  usePortal ? <FloatingPortal>{children}</FloatingPortal> : <>{children}</>;
